import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Users, DollarSign, Target, Percent, BarChart3, TrendingDown, TrendingUp, Activity } from 'lucide-react';
import {
  useAdminData, Metric, LoadingBlock, ErrorBox, ExcludedNote, SectionNote, money, pct, count, isMeasured,
} from '../../components/admin/AdminKit';

// WO-36 — every figure here is measured (see routers/analytics.py). Money comes
// from subscriptions that are live in Stripe; admin-granted plans, our own and
// test accounts are left out. LTV and CAC are not estimated any more.

export default function AdminExecutiveDashboard() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/executive/dashboard');
  if (loading) return <LoadingBlock testid="admin-executive-loading" />;
  const k = data?.kpis || {};
  const dist = data?.access_distribution || {};

  return (
    <div className="space-y-6" data-testid="admin-executive-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Executive Dashboard</h1>
        <p className="text-muted-foreground">The business at a glance — customers only, money from Stripe</p>
      </div>
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <ExcludedNote excluded={data.excluded} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="MRR" icon={DollarSign} value={money(k.mrr)}
              note={`${count(k.paying_subscriptions)} live Stripe subscriptions, list price`} testid="kpi-mrr" />
            <Metric label="ARR" icon={DollarSign} value={money(k.arr)} note="MRR × 12" />
            <Metric label="Paying users" icon={Target} value={count(k.paying_users)}
              note="Personal subscribers + paid Enterprise seats" />
            <Metric label="Customer users" icon={Users} value={count(k.total_users)}
              note={`${count(k.total_companies)} companies`} />
            <Metric label="Free → paid" icon={Percent} value={pct(k.conversion_rate)}
              note="Paying users ÷ customer users" />
            <Metric label="ARPA" icon={BarChart3} value={money(k.arpa)}
              why="Needs at least one paying subscription" note="MRR ÷ paying subscriptions" />
            <Metric label="Cancelled (30 days)" icon={TrendingDown} value={count(k.cancellations_30d)}
              note={isMeasured(k.churn_rate_30d) ? `${k.churn_rate_30d}% of subscriptions` : 'No subscriptions yet'} />
            <Metric label="LTV / CAC" icon={TrendingUp} value={null}
              why="Needs marketing spend and several months of paying history" testid="kpi-ltv" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-primary" />Active users</CardTitle>
                <CardDescription>People who used the app, not only people who typed a password</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {[['Last 24 h', k.dau], ['Last 7 days', k.wau], ['Last 30 days', k.mau]].map(([label, v]) => (
                    <div key={label} className="text-center p-4 rounded-lg bg-muted/30">
                      <p className="text-3xl font-bold text-primary">{count(v)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-500" />Signups</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <Metric label="Last 7 days" value={count(k.new_users_7d)} />
                <Metric label="Last 30 days" value={count(k.new_users_30d)} />
                <Metric label="vs previous 30 days" value={pct(k.growth_rate)}
                  why="The previous 30 days had no signups to compare with" />
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" />What customers can use</CardTitle>
              <CardDescription>
                Access level per person, whoever pays for it. {count(data.granted_without_payment)} of them have
                paid-level access without a Stripe payment (granted by an admin — testers, partners).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['free', 'standard', 'premium', 'enterprise'].map((p) => (
                  <div key={p} className="p-4 rounded-lg bg-muted/30 text-center">
                    <p className="text-2xl font-bold">{count(dist[p] || 0)}</p>
                    <p className="text-sm text-muted-foreground capitalize">{p}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <SectionNote>
            MRR follows Stripe's definition: live subscriptions (active or past due), annual plans divided by 12, at
            list price before referral discounts. Report purchases are one-off and are on the Revenue page.
          </SectionNote>
        </>
      )}
    </div>
  );
}
